import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { adminApiService } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import { SecurityRole } from '../../constants'
import type { AdminUser, AdminUserRoleFilter, AdminUserStatusFilter, PagedResult } from '../../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../../styles/theme'
import { Avatar, RoleChip, UserStatusChip, useDialogs } from '../../components/ui'
import AdminTabs from './AdminTabs'

function roleLabel(role: number): string {
  if (role >= SecurityRole.SUPER_ADMIN) return 'Super Admin'
  if (role >= SecurityRole.TEAM_ADMIN) return 'Team Admin'
  return 'Customer'
}

function toRoleKey(role: number): 'super-admin' | 'team-admin' | 'editor' | 'viewer' {
  if (role >= SecurityRole.SUPER_ADMIN) return 'super-admin'
  if (role >= SecurityRole.TEAM_ADMIN) return 'team-admin'
  return 'viewer'
}

const ROLE_FILTERS: { value: AdminUserRoleFilter | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'super-admin', label: 'Super admins' },
  { value: 'team-admin', label: 'Team admins' },
  { value: 'member', label: 'Members' },
]
const STATUS_FILTERS: { value: AdminUserStatusFilter | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'locked', label: 'Locked' },
  { value: 'invited', label: 'Invited' },
  { value: 'deactivated', label: 'Deactivated' },
]

export default function AdminUsersPage() {
  const dialogs = useDialogs()
  // Filters live in the URL so dashboard cards can link straight to e.g. ?role=super-admin.
  const [params, setParams] = useSearchParams()
  const role = (params.get('role') ?? '') as AdminUserRoleFilter | ''
  const status = (params.get('status') ?? '') as AdminUserStatusFilter | ''
  const query = params.get('q') ?? ''
  const [search, setSearch] = useState(query)
  const [result, setResult] = useState<PagedResult<AdminUser> | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [acting, setActing] = useState<string | null>(null)

  async function load(p: number) {
    setLoading(true)
    setError('')
    try {
      const data = await adminApiService.getUsers(p, 20, { role: role || undefined, status: status || undefined, query: query || undefined })
      setResult(data)
      setPage(p)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1) }, [role, status, query])
  useEffect(() => { setSearch(query) }, [query])
  // Debounced search: typing updates the URL (and so the list) after a short pause.
  useEffect(() => {
    if (search === query) return
    const t = setTimeout(() => setFilter('q', search.trim()), 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  function setFilter(key: 'role' | 'status' | 'q', value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value); else next.delete(key)
    setParams(next, { replace: true })
  }

  async function handleLock(userId: string) {
    if (!(await dialogs.confirm({ title: 'Lock this user?', message: 'They are signed out and cannot sign in until unlocked.', confirmLabel: 'Lock', danger: true }))) return
    setActing(userId)
    try {
      await adminApiService.lockUser(userId)
      load(page)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Lock failed')
    } finally {
      setActing(null)
    }
  }

  async function handleResetMfa(userId: string, email: string) {
    if (!(await dialogs.confirm({ title: `Reset two-factor authentication for ${email}?`, message: 'Only do this after verifying it is really them. They will be signed out everywhere and must set MFA up again.', confirmLabel: 'Reset MFA', danger: true }))) return
    setActing(userId)
    try {
      await adminApiService.resetUserMfa(userId)
      void dialogs.alert({ title: 'MFA reset', message: 'The user has been emailed and signed out.' })
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'MFA reset failed')
    } finally {
      setActing(null)
    }
  }

  async function handleUnlock(userId: string) {
    setActing(userId)
    try {
      await adminApiService.unlockUser(userId)
      load(page)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Unlock failed')
    } finally {
      setActing(null)
    }
  }

  const users = result?.items ?? []
  const total = result?.total ?? 0
  const pageSize = result?.pageSize ?? 20
  const totalPages = Math.ceil(total / pageSize)

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
    fontWeight: 600,
    color: colors.textSecondary,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.base,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }

  return (
    <>
    <div>
      <AdminTabs />

      <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xl }}>
        Users
        {total > 0 && (
          <span style={{ fontSize: fontSize.base, fontWeight: 400, color: colors.textSecondary, marginLeft: spacing.sm }}>
            ({total} total)
          </span>
        )}
      </h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'center', marginBottom: spacing.md }}>
        <div role="group" aria-label="Filter by role" style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
          {ROLE_FILTERS.map(f => {
            const on = role === f.value
            return (
              <button key={f.label} type="button" aria-pressed={on} onClick={() => setFilter('role', f.value)}
                style={{
                  padding: '4px 12px', borderRadius: 999, fontSize: fontSize.sm, cursor: 'pointer',
                  border: `1px solid ${on ? colors.primary : colors.border}`,
                  background: on ? colors.primary : colors.surface, color: on ? '#fff' : colors.text,
                }}>
                {f.label}
              </button>
            )
          })}
        </div>
        <select aria-label="Filter by status" value={status} onChange={e => setFilter('status', e.target.value)}
          style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, background: colors.surface }}>
          {STATUS_FILTERS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <input type="search" aria-label="Search users" placeholder="Search email or name" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '5px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 16, flex: '1 1 180px', minWidth: 0, maxWidth: 320 }} />
      </div>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : users.length === 0 ? (
        <div style={{ color: colors.textSecondary }}>{role || status || query ? 'No users match these filters.' : 'No users found.'}</div>
      ) : (
        <>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflowX: 'auto',
            marginBottom: spacing.md,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name / Email</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Last Login</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.userId}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        <Avatar name={[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email} size={28} />
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                          </div>
                          <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}><RoleChip role={toRoleKey(u.securityRole)} /></td>
                    <td style={tdStyle}><UserStatusChip status={u.isLocked || u.isDeactivated ? 'suspended' : u.isInvited ? 'invited' : 'active'} /></td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td style={tdStyle}>
                      {u.isLocked ? (
                        <button
                          onClick={() => handleUnlock(u.userId)}
                          disabled={acting === u.userId}
                          style={{
                            padding: '3px 8px',
                            background: 'none',
                            border: `1px solid ${colors.primary}`,
                            borderRadius: radius.sm,
                            fontSize: fontSize.sm,
                            cursor: 'pointer',
                            color: colors.primary,
                          }}
                        >
                          {acting === u.userId ? '...' : 'Unlock'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleLock(u.userId)}
                          disabled={acting === u.userId}
                          style={{
                            padding: '3px 8px',
                            background: 'none',
                            border: `1px solid ${colors.error}`,
                            borderRadius: radius.sm,
                            fontSize: fontSize.sm,
                            cursor: 'pointer',
                            color: colors.error,
                          }}
                        >
                          {acting === u.userId ? '...' : 'Lock'}
                        </button>
                      )}
                      <button
                        onClick={() => handleResetMfa(u.userId, u.email)}
                        disabled={acting === u.userId}
                        title="Lost authenticator and recovery codes"
                        style={{
                          marginLeft: 6,
                          padding: '3px 8px',
                          background: 'none',
                          border: `1px solid ${colors.border}`,
                          borderRadius: radius.sm,
                          fontSize: fontSize.sm,
                          cursor: 'pointer',
                          color: colors.textSecondary,
                        }}
                      >
                        Reset MFA
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  opacity: page <= 1 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => load(page + 1)}
                disabled={page >= totalPages}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: page >= totalPages ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
      {dialogs.element}
    </>
  )
}
